import React from 'react';
import { Modal, ModalContent, ModalOverlay } from '@chakra-ui/react';
import { Box } from '../UI-CHAKRA/Box';
import { Flex } from '../UI-CHAKRA/Flex';
import { Text } from '../UI-CHAKRA/Text';
import { Button } from '../UI-CHAKRA/Button';
import { ReactComponent as CloseIcon } from '../../assets/images/icons/mevo/ic-exit-rounded-purple.svg';
import { ReactComponent as InfoIcon } from '../../assets/images/icons/info.svg';
import { TrackerEventService } from '../../containers/prescription/trackerEventService';
import { useIsMobile } from '../../helpers/useIsMobile';
import { getPbmBenefit, PSPBenefit } from '../../interfaces/medicine';
import { logger } from '../../helpers/logger';

interface PbmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  prescription?: {
    idPrescricao: number;
    CodigoValidacao?: string;
    Paciente?: {
      idPaciente: number;
    };
    Prescritor?: {
      idPrescritor: number;
    };
    idPrescritor?: number;
  };
  prescriberConfiguration?: {
    idParceiro?: number;
  };
  medicineData?: {
    IdMedicamento: number;
    ListaEANS?: string[];
    EANs?: string[];
    Beneficios?: PSPBenefit[];
  };
}

const PbmModal: React.FC<PbmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  prescription,
  prescriberConfiguration,
  medicineData,
}) => {
  const isMobile = useIsMobile();
  const listaEans = medicineData?.EANs ?? medicineData?.ListaEANS ?? [];
  const nomeDoPrograma = getPbmBenefit(medicineData?.Beneficios)?.Nome;

  const emitClick = async (interest: boolean) => {
    if (!prescription || !medicineData) return;
    try {
      await TrackerEventService.track(prescription.idPrescricao, 'backoffice_campaign_pbm_modal_continue_click', {
        flow: 'assinar prescrição',
        idPrescricao: prescription?.idPrescricao,
        codigoValidacao: prescription?.CodigoValidacao,
        idParceiro: prescriberConfiguration?.idParceiro,
        idMedico: prescription?.Prescritor?.idPrescritor || prescription?.idPrescritor,
        idPaciente: prescription?.Paciente?.idPaciente,
        idMedicamento: medicineData?.IdMedicamento,
        listaEans,
        nomeDoPrograma,
        isMobile,
        tipoDePrograma: 'PBM',
        interessePreCadastro: interest
      });
    } catch (error) {
      logger.error('[PBM - Emitir prescrição] Erro ao emitir evento de prescrição:', error)
    }
  };

  const handleClose = async () => {
    await emitClick(false);
    onClose();
  };

  const handleConfirm = async () => {
    await emitClick(true);
    onConfirm();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      isCentered={!isMobile}
      motionPreset={isMobile ? 'slideInBottom' : 'scale'}
    >
      <ModalOverlay bg="rgba(0, 0, 0, 0.5)" backdropFilter="blur(2px)" />
      <ModalContent
        maxW={{ base: '100%', md: '500px' }}
        width={{ base: '100%', md: 'auto' }}
        margin={isMobile ? 0 : undefined}
        position={isMobile ? 'fixed' : 'relative'}
        bottom={isMobile ? '0' : undefined}
        left={isMobile ? '0' : undefined}
        right={isMobile ? '0' : undefined}
        borderRadius={isMobile ? '20px 20px 0 0' : '20px'}
        padding={'0px'}
        boxShadow={'0px 0px 20px 7px rgba(0,0,0,0.25)'}
      >
        <Box padding={'24px'} position={'relative'}>
          {isMobile && (
            <Flex justifyContent={'center'} mb={'12px'} mt={'-4px'}>
              <Box width={'48px'} height={'4px'} borderRadius={'full'} backgroundColor={'primary.main'} />
            </Flex>
          )}

          {!isMobile && (
            <Flex position={'absolute'} top={'24px'} left={'24px'} alignItems={'center'} justifyContent={'center'}>
              <Box
                width={'32px'}
                height={'32px'}
                borderRadius={'50%'}
                backgroundColor={'primary.main'}
                display={'flex'}
                alignItems={'center'}
                justifyContent={'center'}
              >
                <InfoIcon width={'20px'} height={'20px'} fill="#FFFFFF" />
              </Box>
            </Flex>
          )}
          <Box position={'absolute'} top={'24px'} right={'24px'}>
            <CloseIcon onClick={handleClose} cursor={'pointer'} />
          </Box>

          <Box mb={'16px'} mt={isMobile ? '8px' : '56px'}>
            <Text fontSize={'20px'} fontWeight={700} color={'#1E252B'} lineHeight={'28px'}>
              Desconto disponível para o paciente
            </Text>
          </Box>

          <Text fontSize={'14px'} color={'#545C62'} mb={'24px'} lineHeight={'20px'}>
            Esta receita possui um medicamento elegível para desconto. O paciente deseja receber informações de como
            obter o benefício?
          </Text>

          <Flex
            gap={'12px'}
            flexDirection={isMobile ? 'column' : 'row'}
            justifyContent={isMobile ? 'stretch' : 'flex-end'}
          >
            <Button
              onClick={handleConfirm}
              order={isMobile ? 0 : 1}
              width={isMobile ? '100%' : 'auto'}
              padding="10px 20px"
              backgroundColor={'primary.main'}
              borderRadius={'20px'}
              _hover={{ backgroundColor: '#6B409C' }}
            >
              <Text fontSize={'14px'} color={'#FFF'}>
                Sim, enviar informações
              </Text>
            </Button>
            <Button
              onClick={handleClose}
              order={isMobile ? 1 : 0}
              width={isMobile ? '100%' : 'auto'}
              padding="10px 20px"
              backgroundColor={'#FFF'}
              borderWidth={'2px'}
              borderColor={'primary.main'}
              borderStyle={'solid'}
              borderRadius={'20px'}
              _hover={{ backgroundColor: '#FFF', borderColor: '#6B409C' }}
            >
              <Text fontSize={'14px'} color={'primary.main'}>
                Não, obrigado
              </Text>
            </Button>
          </Flex>
        </Box>
      </ModalContent>
    </Modal>
  );
};

export default PbmModal;
